import { useState, useEffect } from 'react';
import axios from 'axios';
import { History, User, Calendar, Filter, TrendingUp, Pencil, Trash2, X, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea } from 'recharts';
import { normalizeNote, moyenneNormalisee } from '../utils/normalizeNote';
import { toast } from 'sonner';

const HistoriqueNotes = ({ user }) => {
  const [notes, setNotes] = useState([]);
  const [filteredNotes, setFilteredNotes] = useState([]);
  const [stagiaires, setStagiaires] = useState([]);
  const [filterStagiaire, setFilterStagiaire] = useState('all');
  const [filterCategorie, setFilterCategorie] = useState('all');
  const [filterItem, setFilterItem] = useState('all');
  const [customEpreuves, setCustomEpreuves] = useState([]);
  const [editingNote, setEditingNote] = useState(null);
  const [editValues, setEditValues] = useState({ note: 0, commentaire: '' });

  // Même structure de catégories que dans Notation.js
  const categoriesStructure = {
    'Briefing': ['BRIEFING VNM', 'BRIEFING Engins tractés', 'BRIEFING ESH', 'BRIEFING PNM', 'BRIEFING JET À BRAS'],
    'Encadrement': ['VNM', 'Engins tractés', 'ESH', 'PNM', 'JET À BRAS'],
    'Sécurité': ['VNM', 'JET à bras', 'ESH', 'PNM', 'Bateau', 'Engins tractés'],
    'Pédagogie': ['VNM', 'JET à bras', 'ESH', 'PNM', 'Bateau'],
    'Aisance': ['VNM', 'Jet à bras', 'PNM', 'Engins tractés', 'Bateau'],
    'Procédure d\'intervention': ['VNM', 'JET à bras', 'ESH', 'PNM', 'Bateau'],
    'Animation': ['VNM', 'JET à bras', 'ESH', 'PNM', 'Engins tractés'],
    'Compétences Générales': [
      'Entretien rangement du matériel',
      'Implication générale'
    ],
    'Prérequis': ['Parcours jet à bras', 'Parcours jet à selle', 'Parcours engins tractés'],
    'Épreuves Formatives': [
      'Réglementation',
      'Public',
      'Physio',
      'Anatomie',
      'Météo',
      'Message de secours',
      ...customEpreuves
    ]
  };

  useEffect(() => {
    loadNotes();
    loadStagiaires();
    loadCustomEpreuves();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [notes, filterStagiaire, filterCategorie, filterItem]);

  // Réinitialiser l'item quand la catégorie change
  useEffect(() => {
    setFilterItem('all');
  }, [filterCategorie]);

  const loadNotes = async () => {
    try {
      const response = await axios.get('/notes/all-formateurs');
      setNotes(response.data);
    } catch (error) {
    }
  };

  const loadStagiaires = async () => {
    try {
      const response = await axios.get('/stagiaires');
      setStagiaires(response.data);
    } catch (error) {
    }
  };

  const loadCustomEpreuves = async () => {
    try {
      const response = await axios.get('/epreuves-formatives');
      setCustomEpreuves(response.data.map(e => e.sujet));
    } catch (error) {
    }
  };

  const applyFilters = () => {
    let filtered = [...notes];
    
    if (filterStagiaire !== 'all') {
      filtered = filtered.filter(n => n.stagiaire_id === filterStagiaire);
    }
    
    if (filterCategorie !== 'all') {
      filtered = filtered.filter(n => n.categorie === filterCategorie);
    }
    
    if (filterItem !== 'all') {
      filtered = filtered.filter(n => n.item === filterItem);
    }
    
    setFilteredNotes(filtered);
  };

  const handleEditStart = (note) => {
    setEditingNote(note.note_id);
    setEditValues({ note: note.note, commentaire: note.commentaire || '' });
  };

  const handleEditSave = async (noteId) => {
    try {
      await axios.put(`/notes/${noteId}`, editValues);
      toast.success('Note modifiée');
      setEditingNote(null);
      loadNotes();
    } catch {
      toast.error('Erreur lors de la modification');
    }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Supprimer cette note ?')) return;
    try {
      await axios.delete(`/notes/${noteId}`);
      toast.success('Note supprimée');
      loadNotes();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getScoreColor = (score) => {
    if (score >= 0 && score <= 4) return 'text-red-500 bg-red-50';
    if (score >= 5 && score <= 7) return 'text-orange-500 bg-orange-50';
    return 'text-[#8DC63F] bg-green-50';
  };

  const getScoreLevel = (score) => {
    if (score >= 0 && score <= 4) return 'Non compétent';
    if (score >= 5 && score <= 7) return "En cours d'acquisition";
    return 'Acquis';
  };

  // Items disponibles selon la catégorie sélectionnée
  const availableItems = filterCategorie !== 'all' 
    ? categoriesStructure[filterCategorie] || []
    : [];
  
  // Couleurs pour différencier les catégories
  const categorieColors = {
    'Briefing': { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-700' },
    'Encadrement': { bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-700' },
    'Sécurité': { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-700' },
    'Pédagogie': { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-700' },
    'Aisance': { bg: 'bg-sky-50', border: 'border-sky-300', text: 'text-sky-700' },
    'Procédure d\'intervention': { bg: 'bg-cyan-50', border: 'border-cyan-300', text: 'text-cyan-700' },
    'Animation': { bg: 'bg-orange-50', border: 'border-orange-300', text: 'text-orange-700' },
    'Compétences Générales': { bg: 'bg-purple-50', border: 'border-purple-300', text: 'text-purple-700' },
    'Prérequis': { bg: 'bg-pink-50', border: 'border-pink-300', text: 'text-pink-700' },
    'Épreuves Formatives': { bg: 'bg-indigo-50', border: 'border-indigo-300', text: 'text-indigo-700' },
    'default': { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700' }
  };
  
  const getCategorieColor = (categorie) => {
    return categorieColors[categorie] || categorieColors['default'];
  };

  // Préparer les données pour le graphique de progression
  const prepareChartData = () => {
    const sortedNotes = [...filteredNotes]
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(-30);
    
    return sortedNotes.map((note, i) => ({
      date: new Date(note.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
      dateComplet: new Date(note.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }),
      note: parseFloat(normalizeNote(note).toFixed(1)),
      stagiaire: note.stagiaire_nom || 'Stagiaire',
      categorie: note.categorie,
      item: note.item,
      idx: i
    }));
  };

  // Stats
  const totalNotes = filteredNotes.length;
  const moyenneGenerale = totalNotes > 0 
    ? moyenneNormalisee(filteredNotes).toFixed(1) 
    : 0;

  return (
    <div className="space-y-6" data-testid="historique-notes-page">
      <div>
        <h1 className="text-4xl font-display font-bold text-[#0F172A] mb-2">Historique des Notes</h1>
        <p className="text-slate-600">Toutes les évaluations que vous avez données</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="bg-[#8DC63F]/10 p-2 rounded-lg">
              <History className="h-5 w-5 text-[#8DC63F]" />
            </div>
            <div>
              <p className="text-2xl font-display font-bold text-[#0F172A]">{totalNotes}</p>
              <p className="text-sm text-slate-600">Notes données</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-500/10 p-2 rounded-lg">
              <User className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-display font-bold text-[#0F172A]">
                {[...new Set(filteredNotes.map(n => n.stagiaire_id))].length}
              </p>
              <p className="text-sm text-slate-600">Stagiaires évalués</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="bg-purple-500/10 p-2 rounded-lg">
              <span className="text-purple-500 font-bold">/10</span>
            </div>
            <div>
              <p className="text-2xl font-display font-bold text-[#0F172A]">{moyenneGenerale}</p>
              <p className="text-sm text-slate-600">Moyenne générale</p>
            </div>
          </div>
        </div>
      </div>

      {/* Graphique de Progression */}
      {filteredNotes.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-[#8DC63F]" />
            Progression des Notes
          </h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={prepareChartData()}>
                <ReferenceArea y1={0} y2={4} fill="#EF4444" fillOpacity={0.1} />
                <ReferenceArea y1={4} y2={7} fill="#F59E0B" fillOpacity={0.1} />
                <ReferenceArea y1={7} y2={10} fill="#22C55E" fillOpacity={0.1} />
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis domain={[0, 10]} tick={{ fontSize: 12 }} />
                <Tooltip 
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-lg max-w-xs">
                          <p className="font-semibold text-[#0F172A]">{data.stagiaire || 'Stagiaire'}</p>
                          <p className="text-sm text-slate-600">{data.categorie} &gt; {data.item}</p>
                          <p className="text-lg font-bold text-[#8DC63F]">{data.note}/10</p>
                          <p className="text-xs text-slate-400">{data.dateComplet}</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="note" 
                  stroke="#8DC63F" 
                  strokeWidth={3}
                  dot={{ fill: '#8DC63F', strokeWidth: 2, r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="flex gap-4 mt-4 text-xs">
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-100 rounded"></span> Non compétent (0-4)</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-orange-100 rounded"></span> En cours (5-7)</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-100 rounded"></span> Acquis (8-10)</span>
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
        <div className="flex items-center space-x-2 mb-3">
          <Filter className="h-4 w-4 text-slate-400" />
          <span className="text-sm font-medium text-[#0F172A]">Filtres</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="filter-stagiaire" className="text-xs">Stagiaire</Label>
            <select
              id="filter-stagiaire"
              value={filterStagiaire}
              onChange={(e) => setFilterStagiaire(e.target.value)}
              className="w-full h-9 px-3 py-1 text-sm border border-slate-200 rounded-md"
            >
              <option value="all">Tous les stagiaires</option>
              {stagiaires.map(s => (
                <option key={s.user_id} value={s.user_id}>
                  {s.user_info?.prenom} {s.user_info?.nom}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="filter-categorie" className="text-xs">Catégorie</Label>
            <select
              id="filter-categorie"
              value={filterCategorie}
              onChange={(e) => setFilterCategorie(e.target.value)}
              className="w-full h-9 px-3 py-1 text-sm border border-slate-200 rounded-md"
            >
              <option value="all">Toutes les catégories</option>
              {Object.keys(categoriesStructure).map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="filter-item" className="text-xs">
              Article/Item {filterCategorie !== 'all' && <span className="text-slate-400">({filterCategorie})</span>}
            </Label>
            <select
              id="filter-item"
              value={filterItem}
              onChange={(e) => setFilterItem(e.target.value)}
              className="w-full h-9 px-3 py-1 text-sm border border-slate-200 rounded-md"
              data-testid="filter-item"
              disabled={filterCategorie === 'all'}
            >
              <option value="all">{filterCategorie === 'all' ? 'Sélectionnez d\'abord une catégorie' : 'Tous les articles'}</option>
              {availableItems.map(item => (
                <option key={item} value={item}>{item}</option>
              ))}
            </select>
          </div>
        </div>
        {/* Bouton reset filtres */}
        {(filterStagiaire !== 'all' || filterCategorie !== 'all' || filterItem !== 'all') && (
          <button 
            onClick={() => { setFilterStagiaire('all'); setFilterCategorie('all'); setFilterItem('all'); }}
            className="mt-3 text-xs text-[#8DC63F] hover:underline"
          >
            Réinitialiser les filtres
          </button>
        )}
      </div>

      {/* Liste des notes */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200">
          <h2 className="text-lg font-display font-semibold text-[#0F172A]">
            Notes ({filteredNotes.length})
          </h2>
        </div>
        
        <div className="divide-y divide-slate-200">
          {filteredNotes.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              Aucune note trouvée
            </div>
          ) : (
            filteredNotes.map((note) => {
              const stagiaire = stagiaires.find(s => s.user_id === note.stagiaire_id);
              const colorScheme = getCategorieColor(note.categorie);
              const isEditing = editingNote === note.note_id;
              const canEdit = note.formateur_id === user?.user_id || user?.role === 'centre';
              return (
                <div key={note.note_id} className={`p-4 hover:bg-slate-50 transition-colors border-l-4 ${colorScheme.border}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-1">
                        <span className="font-semibold text-[#0F172A]">
                          {stagiaire?.user_info?.prenom} {stagiaire?.user_info?.nom}
                        </span>
                        <span className={`px-2 py-0.5 text-xs rounded ${colorScheme.bg} ${colorScheme.text}`}>
                          {note.categorie}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600">{note.item}</p>
                      {note.formateur_nom && (
                        <p className="text-xs text-slate-500 mt-0.5">Par : <span className="font-medium text-slate-700">{note.formateur_nom}</span></p>
                      )}
                      {isEditing ? (
                        <div className="mt-2 space-y-2">
                          <div className="flex items-center gap-2">
                            <label className="text-xs text-slate-500">Note :</label>
                            <input type="number" min="0" max={note.bareme || 10} value={editValues.note}
                              onChange={(e) => setEditValues(v => ({ ...v, note: parseInt(e.target.value) || 0 }))}
                              className="w-16 h-7 text-sm border rounded px-2" data-testid="edit-note-input" />
                            <span className="text-xs text-slate-400">/{note.bareme || 10}</span>
                          </div>
                          <textarea value={editValues.commentaire} placeholder="Commentaire..."
                            onChange={(e) => setEditValues(v => ({ ...v, commentaire: e.target.value }))}
                            className="w-full h-14 text-sm border rounded p-2 resize-none" data-testid="edit-commentaire-input" />
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => handleEditSave(note.note_id)} className="bg-[#8DC63F] hover:bg-[#7AB62F] h-7 text-xs" data-testid="save-edit-btn">
                              <Check className="h-3 w-3 mr-1" /> Sauvegarder
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingNote(null)} className="h-7 text-xs" data-testid="cancel-edit-btn">
                              <X className="h-3 w-3 mr-1" /> Annuler
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {note.commentaire && (
                            <p className="text-xs text-slate-500 mt-1 italic">"{note.commentaire}"</p>
                          )}
                        </>
                      )}
                      <p className="text-xs text-slate-400 mt-1 flex items-center">
                        <Calendar className="h-3 w-3 mr-1" />
                        {new Date(note.date).toLocaleDateString('fr-FR', { 
                          day: 'numeric', 
                          month: 'long', 
                          year: 'numeric' 
                        })}
                      </p>
                    </div>
                    <div className="text-right flex flex-col items-end gap-2">
                      <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg ${getScoreColor(normalizeNote(note))}`}>
                        <span className="text-xl font-bold">{normalizeNote(note).toFixed(1)}</span>
                      </div>
                      <p className="text-xs text-slate-500">{getScoreLevel(normalizeNote(note))}</p>
                      {canEdit && !isEditing && (
                        <div className="flex gap-1">
                          <button onClick={() => handleEditStart(note)} className="p-1 rounded hover:bg-blue-50 text-blue-500" title="Modifier" data-testid={`edit-note-${note.note_id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => handleDelete(note.note_id)} className="p-1 rounded hover:bg-red-50 text-red-500" title="Supprimer" data-testid={`delete-note-${note.note_id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default HistoriqueNotes;
